import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { ArrowLeft, Mail, Phone, MapPin, Calendar, ShieldCheck, KeyRound, UserX, Sparkles, Save, Copy, ClipboardList, Bookmark, Bell, Briefcase, Wallet, Users as UsersIcon, Activity } from "lucide-react";

function Row({ icon: Icon, label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
        <p className="text-slate-800 break-all">{value}</p>
      </div>
    </div>
  );
}

export function AdminUserProfileSection({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [editing, setEditing] = useState({ name: "", phone: "", district: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setData(data);
      setEditing({
        name: data.user.name || "",
        phone: data.user.phone || "",
        district: data.user.district || "",
      });
    } catch (e) { toast.error(formatErr(e)); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (payload, msg) => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}`, payload);
      toast.success(msg || "Atualizado.");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const doReset = async () => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`, { send_email: true });
      if (data.email_sent) {
        toast.success(`Email enviado para ${data.email}.`);
      } else if (data.temp_password) {
        setTempPassword(data.temp_password);
      }
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Utilizador anonimizado (RGPD).");
      onBack();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!data) return <div className="text-slate-500 text-sm">A carregar...</div>;
  const { user, activity } = data;
  const isDeleted = !!user.deleted_at;

  return (
    <div className="space-y-6">
      <button
        type="button"
        data-testid="admin-user-profile-back"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar aos utilizadores
      </button>

      <div className="bg-white rounded-xl border border-black/5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 data-testid="admin-user-profile-name" className="font-heading font-black tracking-tighter text-2xl text-slate-900">{user.name || "(sem nome)"}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{user.role}</Badge>
              {!user.active && <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Inativo</Badge>}
              {isDeleted && <Badge className="bg-slate-200 text-slate-500 hover:bg-slate-200">Anonimizado</Badge>}
              {user.premium_until && new Date(user.premium_until) > new Date() && (
                <Badge className="bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100">Premium at\u00e9 {new Date(user.premium_until).toLocaleDateString("pt-PT")}</Badge>
              )}
              {user._demo && <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50">demo</Badge>}
            </div>
          </div>
          {!isDeleted && (
            <div className="flex flex-wrap gap-2">
              {user.active ? (
                <Button data-testid="admin-user-deactivate" size="sm" variant="outline" className="rounded-full" disabled={saving} onClick={() => patch({ active: false }, "Utilizador desativado.")}>Desativar</Button>
              ) : (
                <Button data-testid="admin-user-activate" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" disabled={saving} onClick={() => patch({ active: true }, "Utilizador ativado.")}>Ativar</Button>
              )}
              <Button data-testid="admin-user-reset-password" size="sm" variant="outline" className="rounded-full" onClick={doReset}>
                <KeyRound className="w-3.5 h-3.5 mr-1.5" />Reset password
              </Button>
              <Button data-testid="admin-user-delete" size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => setConfirmDelete(true)}>
                <UserX className="w-3.5 h-3.5 mr-1.5" />Apagar (RGPD)
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          <Row icon={Mail} label="Email" value={user.email} />
          <Row icon={Phone} label="Telem\u00f3vel" value={user.phone} />
          <Row icon={MapPin} label="Distrito" value={user.district} />
          <Row icon={Calendar} label="Registado a" value={user.created_at ? new Date(user.created_at).toLocaleString("pt-PT") : null} />
          <Row icon={ShieldCheck} label="C\u00f3digo de referral" value={user.referral_code} />
          <Row icon={Sparkles} label="Headline" value={user.headline} />
        </div>
      </div>

      {!isDeleted && (
        <div className="bg-white rounded-xl border border-black/5 p-6">
          <p className="font-heading font-bold tracking-tighter text-slate-900 mb-4">Edi\u00e7\u00e3o r\u00e1pida</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input data-testid="admin-user-edit-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nome" />
            <Input data-testid="admin-user-edit-phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="Telem\u00f3vel" />
            <Input data-testid="admin-user-edit-district" value={editing.district} onChange={(e) => setEditing({ ...editing, district: e.target.value })} placeholder="Distrito" />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Button data-testid="admin-user-save" size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={saving}
              onClick={() => patch({ name: editing.name, phone: editing.phone, district: editing.district }, "Guardado.")}>
              <Save className="w-3.5 h-3.5 mr-1.5" />Guardar altera\u00e7\u00f5es
            </Button>
            <div className="h-6 border-l border-slate-200 mx-2" />
            <span className="text-xs text-slate-500">Premium:</span>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => patch({ premium_days: 30 }, "+30 dias de premium")}>+30 dias</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => patch({ premium_days: 90 }, "+90 dias de premium")}>+90 dias</Button>
            {user.premium_until && new Date(user.premium_until) > new Date() && (
              <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => patch({ premium_days: -3650 }, "Premium removido")}>Remover premium</Button>
            )}
            <div className="h-6 border-l border-slate-200 mx-2" />
            <span className="text-xs text-slate-500">Role:</span>
            {["candidate", "company", "admin"].filter(r => r !== user.role).map(r => (
              <Button key={r} size="sm" variant="outline" className="rounded-full text-xs" onClick={() => patch({ role: r }, `Role alterada para ${r}.`)}>→ {r}</Button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {user.role === "candidate" && (
          <>
            <StatMini icon={ClipboardList} label="Candidaturas" value={activity.applications?.length || 0} />
            <StatMini icon={Bookmark} label="Vagas guardadas" value={activity.saved_jobs || 0} />
            <StatMini icon={Bell} label="Alertas" value={activity.alerts?.length || 0} />
            <StatMini icon={Sparkles} label="CV com IA" value={activity.cv?.analysis ? "Sim" : "N\u00e3o"} />
          </>
        )}
        {user.role === "company" && (
          <>
            <StatMini icon={Briefcase} label="Vagas publicadas" value={activity.jobs_count || 0} />
            <StatMini icon={Wallet} label="Wallet SILVER" value={(activity.wallet?.silver || 0) + (activity.wallet?.destaque || 0)} />
            <StatMini icon={Wallet} label="Wallet GOLD" value={(activity.wallet?.gold || 0) + (activity.wallet?.destaque_max || 0)} />
            <StatMini icon={Wallet} label="An\u00e1lises IA" value={activity.wallet?.ai_screening || 0} />
            <StatMini icon={ClipboardList} label="Pagamentos" value={activity.payments?.length || 0} />
          </>
        )}
        <StatMini icon={UsersIcon} label="Referrals" value={activity.referrals || 0} />
        <StatMini icon={Activity} label="Uso IA (24h)" value={activity.ai_usage_24h || 0} />
      </div>

      {activity.applications && activity.applications.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <p className="font-heading font-bold tracking-tighter text-slate-900 mb-3">Candidaturas recentes</p>
          <ul className="space-y-2">
            {activity.applications.slice(0, 10).map((a) => (
              <li key={a.id} className="flex items-center justify-between text-sm border-b border-black/5 pb-2 last:border-0">
                <span className="text-slate-700">{a.job_title || a.job_id}</span>
                <span className="flex items-center gap-2">
                  {a.match_score != null && <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]">Match {a.match_score}</Badge>}
                  <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{a.status}</Badge>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {activity.payments && activity.payments.length > 0 && (
        <div className="bg-white rounded-xl border border-black/5 p-5">
          <p className="font-heading font-bold tracking-tighter text-slate-900 mb-3">Pagamentos</p>
          <ul className="space-y-2">
            {activity.payments.map((p) => (
              <li key={p.id} className="flex items-center justify-between text-sm border-b border-black/5 pb-2 last:border-0">
                <span className="text-slate-700">{p.label || p.tier || p.pack || "Pagamento"}</span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">{(p.amount / 100).toFixed(2)}\u20ac</span>
                  <Badge className={p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>{p.payment_status}</Badge>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirm delete dialog */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apagar utilizador (RGPD)</DialogTitle>
            <DialogDescription>
              Isto anonimiza o utilizador (email, nome, telem\u00f3vel, distrito, CV) mas mant\u00e9m o ID para n\u00e3o partir hist\u00f3rico agregado. Ac\u00e7\u00e3o irrevers\u00edvel.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmDelete(false)}>Cancelar</Button>
            <Button data-testid="admin-user-delete-confirm" className="rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={doDelete}>Sim, anonimizar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password dialog */}
      <Dialog open={!!tempPassword} onOpenChange={(o) => { if (!o) setTempPassword(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password tempor\u00e1ria criada</DialogTitle>
            <DialogDescription>
              O RESEND n\u00e3o est\u00e1 configurado. Copia esta password e entrega-a manualmente ao utilizador. Ele deve alter\u00e1-la no pr\u00f3ximo login.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-4 font-mono text-lg text-center break-all select-all" data-testid="admin-user-temp-password">
            {tempPassword}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { navigator.clipboard.writeText(tempPassword || ""); toast.success("Copiada."); }}>
              <Copy className="w-3.5 h-3.5 mr-1.5" />Copiar
            </Button>
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setTempPassword(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatMini({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-xl border border-black/5 p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
        <Icon className="w-3.5 h-3.5 text-slate-400" />
      </div>
      <p className="font-heading font-black tracking-tighter text-2xl text-slate-900 mt-1">{value}</p>
    </div>
  );
}
